import { ClassificationLabels } from "../../models/classification.map";

export const AVATAR_PALETTE = [
    { bg: "#dbeafe", color: "#1d4ed8" },
    { bg: "#dcfce7", color: "#15803d" },
    { bg: "#fef3c7", color: "#b45309" },
    { bg: "#ede9fe", color: "#7c3aed" },
    { bg: "#fce7f3", color: "#be185d" },
    { bg: "#e0f2fe", color: "#0369a1" },
    { bg: "#fef9c3", color: "#a16207" },
    { bg: "#ffedd5", color: "#c2410c" },
];

const HIDDEN_PATIENT_CLASSIFICATIONS = new Set([
    "ARRET_TTT",
    "PRIMO_MISE_EN_ATTENTE",
    "RAS_NOUVELLE_ORDO",
]);

const DEPART_DC_NAMES = ["Départ - DC", "Départ / DC"];
const DEFAULT_VALIDATION_ERROR = "La validation du patient a échoué. Réessayez.";
const EDITABLE_TAGS = new Set(["input", "select", "textarea"]);
const SHORTCUTS = new Map([["p", "addPatient"], ["m", "addDrug"], ["s", "save"]]);

const pad2 = n => String(n).padStart(2, "0");

export function formatDateTime(str) {
    if (!str) return null;
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return null;
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function getInitials(nom, prenom) {
    const n = (nom || "").trim();
    const p = (prenom || "").trim();
    if (!n && !p) return "?";
    if (!n) return p.slice(0, 2).toUpperCase();
    if (!p) return n.slice(0, 2).toUpperCase();
    return (n[0] + p[0]).toUpperCase();
}

export function computeAge(dateNaissance, now = new Date()) {
    if (!dateNaissance) return null;
    const birth = new Date(dateNaissance);
    if (Number.isNaN(birth.getTime())) return null;
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age >= 0 ? age : null;
}

export function avatarColors(index) {
    return AVATAR_PALETTE[index % AVATAR_PALETTE.length];
}

export function sidebarPatientName(patient, index) {
    return (patient.nom || patient.prenom)
        ? `${patient.nom || ""} ${patient.prenom || ""}`.trim()
        : `Patient ${index + 1}`;
}

export function patientTitle(patient, index) {
    return ((patient.nom || "") + " " + (patient.prenom || "")).trim() || `Patient ${index + 1}`;
}

export function traitementCountLabel(count) {
    return `${count} traitement${count !== 1 ? "s" : ""}`;
}

export function shortClassification(key, labels = ClassificationLabels) {
    if (!key || key === "UNKNOWN") return null;
    return labels[key] || key;
}

export function visibleClassificationEntries(labels = ClassificationLabels) {
    return Object.entries(labels).filter(
        ([k]) =>
            k !== "UNKNOWN"
            && !HIDDEN_PATIENT_CLASSIFICATIONS.has(k)
            && !k.startsWith("URGENCE_CHRONIQUE_")
            && !k.startsWith("DEPART_")
    );
}

let nextMedicamentKey = 1;
let nextPatientKey = 1;

function createMedicamentKey() {
    return `medicament-local-${Date.now()}-${nextMedicamentKey++}`;
}

function createPatientKey() {
    return `patient-local-${Date.now()}-${nextPatientKey++}`;
}

export function createEmptyPatient() {
    return {
        _clientKey: createPatientKey(),
        _createdInCurrentDraft: true,
        nom: "",
        prenom: "",
        classification: "",
        produits: [],
        collapsed: false,
        managementValidated: false,
        pharmaceuticalValidated: false,
    };
}

export function createEmptyMedicament(defaultDestination) {
    return {
        _clientKey: createMedicamentKey(),
        nom: "",
        dosage: "",
        posologie: "",
        quantite: 1,
        destination: defaultDestination,
        billingValidated: false,
        planificationValidated: false,
        managementValidated: false,
        pharmaceuticalValidated: false,
    };
}

function createDepartDcProduit(defaultDestination) {
    return {
        _clientKey: createMedicamentKey(),
        nom: "Départ - DC",
        dosage: "",
        posologie: "",
        quantite: 1,
        destination: defaultDestination || "ROBOT",
    };
}

export function withPatientAt(patients, index, update) {
    const copy = [...patients];
    copy[index] = update(copy[index]);
    return copy;
}

// Ajouter un traitement rend les validations agrégées du patient obsolètes,
// sans modifier les validations déjà acquises par ses traitements existants.
export function invalidatePatientAfterProductAddition(previous, updated) {
    const previousCount = previous?.produits?.length || 0;
    const updatedCount = updated?.produits?.length || 0;
    if (updatedCount <= previousCount) return updated;
    return {
        ...updated,
        managementValidated: false,
        managementValidatedAt: null,
        managementValidatedBy: null,
        pharmaceuticalValidated: false,
        pharmaceuticalValidatedAt: null,
        pharmaceuticalValidatedBy: null,
    };
}

const DETACHED_IDENTITY = {
    // Modifier manuellement le nom ou le prénom signifie que la ligne vise un
    // autre patient, jamais que l'on renomme la fiche patient actuellement liée.
    id: null,
    _createdInCurrentDraft: false,
    _identityResolution: undefined,
    _identityCandidateId: undefined,
};

export function applyPatientFieldUpdate(patient, field, value, defaultDestination) {
    const identityChanged = (field === "nom" || field === "prenom") && value !== patient?.[field];
    const updated = { ...patient, [field]: value, ...(identityChanged ? DETACHED_IDENTITY : {}) };
    if (field !== "classification" || value !== "DEPART_DC") return updated;
    const produits = updated.produits || [];
    if (produits.some(p => DEPART_DC_NAMES.includes(p.nom))) return updated;
    return { ...updated, produits: [...produits, createDepartDcProduit(defaultDestination)] };
}

export function applyAutocompleteSelection(patient, suggestion) {
    return {
        ...patient,
        id: suggestion.id ?? null,
        _createdInCurrentDraft: false,
        nom: suggestion.nom || "",
        prenom: suggestion.prenom || "",
        dateNaissance: suggestion.dateNaissance || "",
        _identityResolution: undefined,
        _identityCandidateId: undefined,
    };
}

const VALIDATION_FIELDS = {
    billing: { flag: "billingValidated", at: "billingHour", by: "billingUsername" },
    planification: { flag: "planificationValidated", at: "planificationValidatedAt", by: "planificationValidatedBy" },
};

export function validationUsername(user) {
    return user?.username || user?.email || "";
}

function hourTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function validationValues(kind, checked, now, username) {
    const { flag, at, by } = VALIDATION_FIELDS[kind];
    return { [flag]: checked, [at]: now, [by]: username };
}

export function applyProduitValidation(produits, selection, { kind, checked, now, username }) {
    const selectedIds = new Set(selection.map(m => m.id).filter(Boolean));
    const selectedRefs = new Set(selection);
    const values = validationValues(kind, checked, now, username);
    if (kind === "billing") values.billingHourTime = hourTime(now);
    return produits.map(m => {
        if (!(m.id ? selectedIds.has(m.id) : selectedRefs.has(m))) return m;
        return { ...m, ...values };
    });
}

export function validationOverridesFor(previous, selection, { kind, checked, now, username }) {
    const next = { ...previous };
    const values = validationValues(kind, checked, now, username);
    selection.filter(m => m.id).forEach(m => { next[m.id] = { ...next[m.id], ...values }; });
    return next;
}

export function summarizeValidation(produits, overrides, kind) {
    const { flag, at, by } = VALIDATION_FIELDS[kind];
    const allValidated = produits.every(p => {
        const ov = overrides[p.id];
        return ov?.[flag] !== undefined ? ov[flag] : !!p[flag];
    });
    const refProd = produits.find(p => overrides[p.id]?.[at] || p[at]) || produits[0];
    return {
        allValidated,
        refHour: overrides[refProd?.id]?.[at] ?? refProd?.[at],
        refUser: overrides[refProd?.id]?.[by] ?? refProd?.[by],
    };
}

export function validationErrorMessage(error) {
    return error?.response?.data?.message
        || error?.response?.data?.detail
        || DEFAULT_VALIDATION_ERROR;
}

export function resolveShortcut(event) {
    if (!(event.ctrlKey || event.metaKey)) return null;
    return SHORTCUTS.get(String(event.key).toLowerCase()) || null;
}

export function isEditableTag(tagName) {
    return EDITABLE_TAGS.has(String(tagName).toLowerCase());
}
